import apiClient from './client'

const BASE = '/api/admin/tracking'

export interface Matter {
  matter_id: string
  title: string
  status: 'waiting' | 'our_move' | 'closed'
  ask: string
  institution: string | null
  institution_id: string | null
  due_on: string | null
  deadline_kind: 'statutory' | 'our target' | null
  legal_basis: string
  days_overdue: number
  next_step: string
  owner: string | null
  watch_terms: string[]
  closure: string | null
  closure_note: string | null
  letters: number
  replies: number
  new_replies: number
  opened: string
  created_by: string
  timeline?: TimelineItem[]
  outcomes?: { id: string; title: string; kind: string; occurred_on: string; reference: string }[]
}

export interface TimelineItem {
  kind: 'note' | 'status' | 'letter' | 'reply'
  date: string | null
  text: string
  by?: string
  to?: string
  from?: string
  status?: string
  email_id?: string
  reply_id?: string
}

export interface MatterCandidate {
  email_id: string
  subject: string
  to: string
  institution: string | null
  institution_id: string | null
  sent: string
  days_silent: number
}

export interface Outcome {
  outcome_id: string
  title: string
  kind: string
  occurred_on: string
  institution: string | null
  institution_id: string | null
  reference: string
  description: string
  reply_id: string | null
  source_url: string | null
  matter_id: string | null
  is_public: boolean
  created_by: string
}

export interface FoiRequest {
  email_id: string
  matter_id: string | null
  subject: string
  institution: string | null
  signer: string | null
  sent: string
  deadline: string
  days_left: number
  overdue: boolean
  extension_notice_by: string
  latest_with_extension: string
  answered: boolean
  closed: boolean
}

export interface OverdueLetter {
  email_id: string
  subject: string
  to: string
  institution: string | null
  sent: string
  reply_by: string
  days_overdue: number
  is_foi: boolean
}

export interface SignerProfile {
  id: string
  key: string
  name: string
  postal_address: string
  reply_email: string
  active: boolean
}

export interface Subscriber {
  email: string
  name: string
  institution: string | null
  since: string
  note: string
}

export const OUTCOME_KINDS: Record<string, string> = {
  formal_act: 'Formal act',
  case_opened: 'Case opened',
  foi_answer: 'FOI answer',
  meeting: 'Meeting',
  referral: 'Referred onward',
  public_position: 'Public position',
  policy_uptake: 'Reached a policy text',
  other: 'Other'
}

export const CLOSURES: Record<string, string> = {
  answered: 'Answered',
  partly_answered: 'Partly answered',
  refused: 'Refused',
  moot: 'Overtaken by events',
  escalated: 'Escalated',
  dropped: 'Dropped'
}

export const trackingApi = {
  listMatters: (status = 'open') => apiClient.get<Matter[]>(`${BASE}/matters`, { params: { status } }),
  readMatter: (id: string) => apiClient.get<Matter>(`${BASE}/matters/${id}`),
  candidates: (serbianOnly = true) =>
    apiClient.get<MatterCandidate[]>(`${BASE}/matters/candidates`, { params: { serbian_only: serbianOnly } }),
  openMatter: (data: Record<string, unknown>) => apiClient.post(`${BASE}/matters`, data),
  updateMatter: (id: string, data: Record<string, unknown>) => apiClient.patch<Matter>(`${BASE}/matters/${id}`, data),
  closeMatter: (id: string, closure: string, note: string) =>
    apiClient.post(`${BASE}/matters/${id}/close`, { closure, note }),

  listOutcomes: (kind?: string) => apiClient.get<Outcome[]>(`${BASE}/outcomes`, { params: kind ? { kind } : {} }),
  logOutcome: (data: Record<string, unknown>) => apiClient.post(`${BASE}/outcomes`, data),
  updateOutcome: (id: string, data: Record<string, unknown>) => apiClient.patch(`${BASE}/outcomes/${id}`, data),
  deleteOutcome: (id: string) => apiClient.delete(`${BASE}/outcomes/${id}`),

  setContactStatus: (data: { email: string; status: string | null; note?: string; source_reply_id?: string }) =>
    apiClient.put(`${BASE}/contacts/status`, data),
  contactsByStatus: (status: string) => apiClient.get(`${BASE}/contacts`, { params: { status } }),

  foiDeadlines: () => apiClient.get<FoiRequest[]>(`${BASE}/foi`),
  overdueLetters: () => apiClient.get<OverdueLetter[]>(`${BASE}/overdue-letters`),
  signers: () => apiClient.get<SignerProfile[]>(`${BASE}/signers`),
  saveSigner: (data: { key: string; name: string; postal_address: string; reply_email?: string; active?: boolean }) =>
    apiClient.put(`${BASE}/signers`, data),
  subscribers: () => apiClient.get<Subscriber[]>(`${BASE}/subscribers`),
  addSubscriber: (data: { email: string; source_reply_id: string; note?: string }) =>
    apiClient.post(`${BASE}/subscribers`, data),
  removeSubscriber: (email: string) => apiClient.delete(`${BASE}/subscribers/${encodeURIComponent(email)}`)
}
